import type { KBTemplate } from "@/lib/pharmacologyKnowledgeBase";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { normalize } from "@/lib/pharmacology/aliasResolver";

export type SubstanceRow = Record<string, any> & {
  id: string;
  name: string;
  owner_id?: string | null;
  is_global?: boolean;
};

export type SubstanceGroup = {
  key: string;
  canonicalName: string;
  primary: SubstanceRow;
  duplicates: SubstanceRow[];
  all: SubstanceRow[];
  template: KBTemplate | null;
  aliases: string[];
  completeness: number;
};

const ARRAY_FIELDS = [
  "brand_names",
  "brands",
  "reference_names",
  "synonyms",
  "common_misspellings",
  "therapeutic_areas",
  "available_formulations",
] as const;

export function canonicalText(value: unknown): string {
  return normalize(String(value ?? ""));
}

export function uniqueStrings(values: unknown[]): string[] {
  const map = new Map<string, string>();
  for (const value of values.flatMap((v) => Array.isArray(v) ? v : [v])) {
    const text = String(value ?? "").trim();
    const key = canonicalText(text);
    if (key && !map.has(key)) map.set(key, text);
  }
  return [...map.values()];
}

export function templateTokens(template: KBTemplate): string[] {
  return uniqueStrings([
    template.name,
    template.generic_name,
    template.normalized_key,
    template.match,
    template.brand_names,
    template.brands,
    template.international_brand_names,
    template.reference_names,
    template.synonyms,
    template.common_misspellings,
  ]);
}

export function substanceTokens(substance: Record<string, any>): string[] {
  return uniqueStrings([
    substance.name,
    substance.generic_name,
    ...ARRAY_FIELDS.map((field) => substance[field]),
  ]);
}

export function buildTemplateAliasIndex(templates: KBTemplate[] = getRuntimeKnowledgeBase()) {
  const index = new Map<string, KBTemplate>();
  for (const template of templates) {
    for (const token of templateTokens(template)) {
      const key = canonicalText(token);
      if (!key) continue;
      const previous = index.get(key);
      if (!previous || template.name.length < previous.name.length) index.set(key, template);
    }
  }
  return index;
}

export function templateForSubstance(
  substance: Record<string, any>,
  templates: KBTemplate[] = getRuntimeKnowledgeBase(),
  aliasIndex = buildTemplateAliasIndex(templates),
): KBTemplate | null {
  const tokens = substanceTokens(substance);
  for (const token of tokens) {
    const hit = aliasIndex.get(canonicalText(token));
    if (hit) return hit;
  }
  return null;
}

export function canonicalKeyForSubstance(
  substance: Record<string, any>,
  templates: KBTemplate[] = getRuntimeKnowledgeBase(),
  aliasIndex = buildTemplateAliasIndex(templates),
): string {
  const template = templateForSubstance(substance, templates, aliasIndex);
  return canonicalText(template?.normalized_key ?? template?.name ?? substance?.name ?? substance?.generic_name ?? substance?.id);
}

export function substanceCompleteness(substance: Record<string, any>): number {
  const fields = [
    "generic_name", "pharmacological_class", "clinical_category", "mechanism_summary",
    "mechanism_expanded", "absorption_notes", "distribution_notes", "metabolism_notes",
    "elimination_notes", "default_curve_model", "onset_min_value", "peak_min_value",
    "total_duration_max_value", "half_life_max_value", "safety_notes", "evidence_level",
    "source_references", "clinical_effect_profile", "adverse_effect_profile",
  ];
  const present = fields.filter((field) => {
    const value = substance?.[field];
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === "object") return Object.keys(value).length > 0;
    return value !== null && value !== undefined && value !== "";
  }).length;
  return Math.round((present / fields.length) * 100);
}

function primaryScore(substance: SubstanceRow, canonicalName: string, userId?: string | null): number {
  let score = substanceCompleteness(substance);
  if (userId && substance.owner_id === userId) score += 40;
  if (substance.is_global) score += 15;
  if (canonicalText(substance.name) === canonicalText(canonicalName)) score += 30;
  if (String(substance.review_status ?? "").toLowerCase().includes("revisada")) score += 12;
  if (!substance.requires_clinical_review) score += 5;
  return score;
}

export function groupSubstances(
  substances: SubstanceRow[],
  options: { templates?: KBTemplate[]; userId?: string | null } = {},
): SubstanceGroup[] {
  const templates = options.templates ?? getRuntimeKnowledgeBase();
  const aliasIndex = buildTemplateAliasIndex(templates);
  const map = new Map<string, SubstanceRow[]>();

  for (const substance of substances.filter((s) => s?.id && s?.name)) {
    const key = canonicalKeyForSubstance(substance, templates, aliasIndex) || canonicalText(substance.id);
    const rows = map.get(key) ?? [];
    rows.push(substance);
    map.set(key, rows);
  }

  return [...map.entries()].map(([key, rows]) => {
    const template = templateForSubstance(rows[0], templates, aliasIndex);
    const canonicalName = template?.name ?? rows.find((r) => canonicalText(r.name) === key)?.name ?? rows[0].name;
    const sorted = [...rows].sort((a, b) => primaryScore(b, canonicalName, options.userId) - primaryScore(a, canonicalName, options.userId));
    const primary = sorted[0];
    return {
      key,
      canonicalName,
      primary,
      duplicates: sorted.slice(1),
      all: sorted,
      template,
      aliases: uniqueStrings(sorted.flatMap(substanceTokens)),
      completeness: substanceCompleteness(primary),
    };
  }).sort((a, b) => a.canonicalName.localeCompare(b.canonicalName, "pt-BR"));
}

export function findExistingCanonical(
  substances: SubstanceRow[],
  template: KBTemplate,
  userId?: string | null,
): SubstanceGroup | null {
  const target = canonicalText(template.normalized_key ?? template.name);
  return groupSubstances(substances, { userId }).find((group) => group.key === target) ?? null;
}

export function filterSubstanceGroups(groups: SubstanceGroup[], query: string): SubstanceGroup[] {
  const q = canonicalText(query);
  if (!q) return groups;
  return groups.filter((group) =>
    [group.canonicalName, group.primary.name, group.primary.generic_name, group.aliases]
      .flatMap((v) => Array.isArray(v) ? v : [v])
      .some((value) => canonicalText(value).includes(q)),
  );
}

export function filterKnowledgeTemplates(templates: KBTemplate[], query: string): KBTemplate[] {
  const q = canonicalText(query);
  if (!q) return templates;
  return templates.filter((template) => templateTokens(template).some((token) => canonicalText(token).includes(q)));
}

export function mergeAliasFields(base: Record<string, any>, incoming: Record<string, any>) {
  return {
    brand_names: uniqueStrings([base.brand_names, base.brands, incoming.brand_names, incoming.brands]),
    brands: uniqueStrings([base.brands, base.brand_names, incoming.brands, incoming.brand_names]),
    reference_names: uniqueStrings([base.reference_names, incoming.reference_names]),
    synonyms: uniqueStrings([base.synonyms, incoming.synonyms]),
    common_misspellings: uniqueStrings([base.common_misspellings, incoming.common_misspellings]),
  };
}
